/**
 * F11 — Panel de gestión de un club FUSIÓN (solo admins).
 *
 * Un club fusión es la unión temporal de 2+ clubes reales para un evento.
 * Este panel permite:
 *   1. Agregar miembros desde los clubes de origen (membresía ADITIVA:
 *      la del club real no se toca, y `originClubId` recuerda de dónde vino).
 *   2. Disolver la fusión: cada miembro vuelve a su club de origen o su
 *      membresía de fusión se apaga si conserva la del club real. Nadie se
 *      borra — el caso "FUSIÓN COAL GEDEÓN" (21 fichas atrapadas) nació de
 *      disolver a mano borrando members.
 */
import { useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
} from "@/components/ui";
import { Users, Merge, AlertTriangle, Loader2, Undo2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useClub } from "@/hooks/useOrganization";
import { useMembersByClub, useAddToFusion } from "@/hooks/usePersons";
import { useAuthStore } from "@/stores/authStore";
import { clubService } from "@/lib/services/organization";
import type { Club, Member } from "@/types";

interface FusionPanelProps {
  club: Club;
  /**
   * F11: modo director de club de ORIGEN — solo ve el bloque de SU club para
   * agregar a su gente. Sin disolver (eso es del admin).
   */
  originOnly?: string;
}

function SourceClubMembers({
  sourceClubId,
  fusionClubId,
  fusionPersonIds,
}: {
  sourceClubId: string;
  fusionClubId: string;
  fusionPersonIds: Set<string>;
}) {
  const { data: sourceClub } = useClub(sourceClubId);
  const { data: members } = useMembersByClub(sourceClubId);
  const addMutation = useAddToFusion();
  const { user } = useAuthStore();
  const [busyId, setBusyId] = useState<string | null>(null);

  const candidates = useMemo(
    () =>
      (members || []).filter(
        (m) =>
          m.status === "active" &&
          m.personId &&
          !fusionPersonIds.has(m.personId),
      ),
    [members, fusionPersonIds],
  );

  const addOne = async (m: Member) => {
    if (!user) return;
    setBusyId(m.id);
    try {
      await addMutation.mutateAsync({
        sourceMember: m,
        fusionClubId,
        userId: user.uid,
      });
      toast.success(`${m.personName || "Miembro"} agregado a la fusión`);
    } finally {
      setBusyId(null);
    }
  };

  const addAll = async () => {
    if (!user) return;
    setBusyId("__all__");
    let ok = 0;
    for (const m of candidates) {
      try {
        await addMutation.mutateAsync({
          sourceMember: m,
          fusionClubId,
          userId: user.uid,
        });
        ok++;
      } catch {
        // el hook ya muestra el toast de error; seguimos con los demás
      }
    }
    toast.success(`${ok} miembro(s) de ${sourceClub?.name || "…"} agregados`);
    setBusyId(null);
  };

  return (
    <div className="rounded-xl border border-gray-200 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="font-medium text-sm text-gray-900 truncate">
          {sourceClub?.name || sourceClubId}
        </p>
        <Button
          size="sm"
          variant="secondary"
          disabled={candidates.length === 0 || busyId !== null}
          onClick={addAll}
        >
          {busyId === "__all__" ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            `Agregar todos (${candidates.length})`
          )}
        </Button>
      </div>
      {candidates.length === 0 ? (
        <p className="text-xs text-gray-500">
          Todos sus miembros activos ya están en la fusión.
        </p>
      ) : (
        <ul className="space-y-1 max-h-44 overflow-y-auto">
          {candidates.map((m) => (
            <li
              key={m.id}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <span className="truncate">{m.personName || m.id}</span>
              <button
                onClick={() => addOne(m)}
                disabled={busyId !== null}
                className="text-xs text-purple-600 hover:text-purple-800 disabled:opacity-40 shrink-0"
              >
                {busyId === m.id ? "…" : "Agregar"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function FusionPanel({ club, originOnly }: FusionPanelProps) {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const { data: fusionMembers } = useMembersByClub(club.id);
  const [dissolving, setDissolving] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const fusionPersonIds = useMemo(
    () =>
      new Set(
        (fusionMembers || [])
          .filter((m) => m.status === "active" && m.personId)
          .map((m) => m.personId as string),
      ),
    [fusionMembers],
  );

  const handleDissolve = async () => {
    if (!user) return;
    setDissolving(true);
    const t = toast.loading("Disolviendo la fusión…");
    try {
      const res = await clubService.dissolveFusion(club.id, user.uid);
      const parts = [
        `${res.deactivated} conservan su club de origen`,
        `${res.returned} trasladados de vuelta`,
      ];
      if (res.dissolved) {
        toast.success(`Fusión disuelta: ${parts.join(" · ")}`, {
          id: t,
          duration: 8000,
        });
      } else {
        console.warn("[dissolveFusion] sin resolver:", res.unresolved);
        toast.error(
          `La fusión SIGUE ACTIVA: ${res.unresolved.length} miembro(s) sin club a dónde volver (${res.unresolved.join(", ")}). Trasládalos manualmente y vuelve a disolver.`,
          { id: t, duration: 12000 },
        );
      }
      await queryClient.invalidateQueries();
      setShowConfirm(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al disolver", { id: t });
    } finally {
      setDissolving(false);
    }
  };

  if (club.status === "inactive") {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="py-6 flex items-center gap-3 text-gray-600">
          <Merge className="w-5 h-5" />
          <p className="text-sm">
            Esta fusión fue disuelta
            {club.dissolvedAt
              ? ` el ${(
                  (club.dissolvedAt as unknown as { toDate?: () => Date })
                    .toDate?.() ?? new Date(club.dissolvedAt as unknown as string)
                ).toLocaleDateString("es-CO")}`
              : ""}
            . Sus miembros volvieron a sus clubes de origen.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg border-l-4 border-l-amber-400">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5 text-amber-600" />
              Club fusión
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Unión temporal de {club.fusionOf?.length || 0} clubes.
              Los miembros conservan su club real; aquí solo participan del
              evento.
            </p>
          </div>
          <Badge className="bg-amber-100 text-amber-800">
            <Users className="w-3.5 h-3.5 mr-1" />
            {fusionPersonIds.size} en la fusión
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          {(club.fusionOf || [])
            .filter((id) => !originOnly || id === originOnly)
            .map((sourceId) => (
            <SourceClubMembers
              key={sourceId}
              sourceClubId={sourceId}
              fusionClubId={club.id}
              fusionPersonIds={fusionPersonIds}
            />
          ))}
        </div>

        {originOnly && (
          <p className="text-xs text-gray-500">
            Agrega aquí a los miembros de tu club que participarán con la
            fusión. Conservan su membresía en tu club; la disolución al final
            la hace la Asociación.
          </p>
        )}
        {!originOnly && (
        <div className="rounded-xl border border-red-200 bg-red-50/60 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">
                Disolver la fusión
              </p>
              <p className="text-xs text-red-800 mt-0.5">
                Cada miembro vuelve a su club de origen. Nadie se elimina; las
                inscripciones y pagos del evento quedan como historial de la
                fusión.
              </p>
              {!showConfirm ? (
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2 text-red-700 border-red-300 hover:bg-red-100"
                  onClick={() => setShowConfirm(true)}
                >
                  <Undo2 className="w-3.5 h-3.5 mr-1" />
                  Disolver…
                </Button>
              ) : (
                <div className="mt-2 space-y-2">
                  <p className="text-xs text-red-900">
                    Escribe <b>DISOLVER</b> para confirmar:
                  </p>
                  <div className="flex gap-2">
                    <input
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      className="px-2 py-1 text-sm border-2 border-red-300 rounded-lg w-36"
                      placeholder="DISOLVER"
                    />
                    <Button
                      size="sm"
                      disabled={confirmText !== "DISOLVER" || dissolving}
                      onClick={handleDissolve}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      {dissolving ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        "Confirmar"
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setShowConfirm(false);
                        setConfirmText("");
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  );
}
